import {
  UsersHeader,
  UsersForm,
  AddPackageDialog,
  ChangeBillingTerms,
  PaymentsAccess,
  UserConsent,
  UserNotes,
} from '@features/admin/users';
import { Tab } from '@features/ui';
import { TTab } from '@src/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserDetailQuery, UserProfile } from '@src/types/users.ts';
import { useLocation, useParams } from 'react-router-dom';
import { addPackageToUser, getBrowseUserDetail } from '@src/services/users.ts';
import { useCountryOptions } from '@hooks/useCountryOptions.ts';
import { useStateOptions } from '@src/hooks/useStateOptions';

export const BrowseUserDetailPage = () => {
  const [tabActive, setTabActive] = useState(0);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [addPackageOpen, setAddPackageOpen] = useState(false);

  const params = useParams();
  const location = useLocation();
  const { countryOptions } = useCountryOptions();
  const { stateOptions } = useStateOptions();
  const { t } = useTranslation();

  const targetInvoiceId = useMemo(() => {
    const match = location.hash.match(/^#invoice-(\d+)$/);

    if (!match) return null;

    return Number(match[1]);
  }, [location.hash]);

  const fetUserDetail = useCallback(
    async (id: number, userQuery?: UserDetailQuery) => {
      const res = await getBrowseUserDetail(id, userQuery);

      setUser(res.data);
    },
    []
  );

  useEffect(() => {
    if (!params.id) return;
    const id = Number(params.id);

    fetUserDetail(id, {
      show_evals: true,
      show_pas: true,
      live_only: true,
    }).then();
  }, [params.id, fetUserDetail]);

  useEffect(() => {
    if (!params.id) return;
    const el = document.getElementById('user-info');

    if (el) {
      el.scrollIntoView({ behavior: 'instant', block: 'start' });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [params.id]);

  useEffect(() => {
    if (targetInvoiceId) {
      setTabActive(1);
    }
  }, [targetInvoiceId]);

  const handleAddPackage = useCallback(
    async (data: {
      product_id: number;
      leeloo_user_id: number;
      valid_from: string;
      valid_to: string;
      reason?: string;
      send_mail_new_account?: boolean;
    }) => {
      await addPackageToUser(data);
      if (!params.id) return;
      const id = Number(params.id);

      await fetUserDetail(id, {
        show_evals: true,
        show_pas: true,
        live_only: true,
      });
    },
    [fetUserDetail, params?.id]
  );

  const tabs: TTab[] = useMemo(
    () => [
      {
        label: 'User Info',
        component: (
          <UsersForm
            countryOptions={countryOptions}
            stateOptions={stateOptions}
            user={user}
          />
        ),
      },
      {
        label: t('users.tab.paymentsAccess'),
        component: (
          <PaymentsAccess targetInvoiceId={targetInvoiceId} user={user} />
        ),
      },
      // {
      //   label: t('users.tab.accessLog'),
      //   component: <AccessLog />,
      // },
      // {
      //   label: t('users.tab.mailQueue'),
      //   component: <MailQueue />,
      // },
      // {
      //   label: t('users.tab.accountActivity'),
      //   component: (
      //     <AccountActivity
      //       refreshUser={async (userQuery) => {
      //         if (!params.id) return;
      //         await fetUserDetail(Number(params.id), userQuery);
      //       }}
      //       user={user}
      //     />
      //   ),
      // },
      // {
      //   label: t('users.tab.notes', { total: notes?.length }),
      //   component: (
      //     <Notes
      //       filterNotes={setNotes}
      //       notes={notes ?? []}
      //       refreshNotes={async () => {
      //         if (params.id) {
      //           await fetchNotes(Number(params.id));
      //         }
      //       }}
      //       userId={params.id ? Number(params.id) : 0}
      //     />
      //   ),
      // },
      {
        label: t('users.tab.userConsent'),
        component: user?.id ? <UserConsent userId={user.id} /> : null,
      },
      {
        label: 'Notes',
        component: user?.id ? <UserNotes userId={user.id} /> : null,
      },
      {
        label: t('users.tab.changeBillingTerms'),
        component: <ChangeBillingTerms />,
      },
    ],
    [countryOptions, stateOptions, user, t]
  );

  return (
    <div>
      <div id="user-info">
        <UsersHeader user={user} />
      </div>
      <div className={'flex gap-[15px] xl:flex-row flex-col flex-1'}>
        <div className={'mt-[23px] relative flex-1'}>
          <Tab
            className={'mb-5'}
            tabActive={tabActive}
            tabs={tabs}
            onChangeTab={setTabActive}
          />
        </div>
      </div>

      <AddPackageDialog
        open={addPackageOpen}
        userId={user?.id ?? null}
        onOpenChange={setAddPackageOpen}
        onSave={handleAddPackage}
      />
    </div>
  );
};
